import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { isDefined } from '../../../utils/variables';
import { DropdownSuggestion } from '../dropdown-suggestion/dropdown-suggesion';
import { Hyperlink } from '../hyperlink/hyperlink';
import { InfoBox } from '../info-box/info-box';
import { LabelledContainer } from '../labelled-container/labelled-container';
import { OverlayLoader } from '../overlay-loader/overlay-loader';
import {
  CoordinateSystemItem,
  useCoordinateSystemsAndGeoidsQuery,
} from '../project-settings/projection';
import { Select } from '../select/select';
import { Stack } from '../stack/stack';

export type CoordinateSystemSelectorProps = {
  disabled?: boolean;
  onCoordinateSystemChange?: (id: string | null) => void;
  onGeoidChange?: (id: string | null) => void;
};

const ALL_COUNTRIES_ID = 'all';

function getFilteredItems(
  items: CoordinateSystemItem[],
  selected: CoordinateSystemItem | undefined,
  countryId: string,
): CoordinateSystemItem[] {
  const filtered =
    countryId === ALL_COUNTRIES_ID
      ? items.filter((x) => !isDefined(x.countryId))
      : items.filter((x) => x.countryId === countryId);

  // Keep the selected item visible even if it falls outside the current country filter.
  if (isDefined(selected) && !filtered.find((x) => x.id === selected.id)) {
    return [selected, ...filtered];
  }
  return filtered;
}

const GeoidAttribution = ({ geoid }: { geoid?: CoordinateSystemItem }) => {
  const { t } = useTranslation();

  const items = useMemo(() => {
    const result: { title: string; url: string; name: string }[] = [];

    if (isDefined(geoid?.sourceName) && isDefined(geoid?.sourceUrl)) {
      result.push({
        title: t('projectSettings.projection.geoidModelSource', { ns: 'components' }),
        url: geoid.sourceUrl,
        name: geoid.sourceName,
      });
    }

    if (isDefined(geoid?.licenseName) && isDefined(geoid?.licenseUrl)) {
      result.push({
        title: t('projectSettings.projection.geoidModelLicense', { ns: 'components' }),
        url: geoid.licenseUrl,
        name: geoid.licenseName,
      });
    }

    return result;
  }, [geoid, t]);

  if (items.length === 0) {
    return null;
  }

  return (
    <InfoBox color="yellow" topMargin={true}>
      <table>
        {items.map((item) => (
          <tr key={item.title}>
            <td>{item.title}</td>
            <td>
              <Hyperlink target="_blank" url={item.url}>
                {item.name}
              </Hyperlink>
            </td>
          </tr>
        ))}
      </table>
    </InfoBox>
  );
};

export const CoordinateSystemSelector = ({
  disabled,
  onCoordinateSystemChange,
  onGeoidChange,
}: CoordinateSystemSelectorProps) => {
  const { t, i18n } = useTranslation();
  const { data, isLoading, errorList } = useCoordinateSystemsAndGeoidsQuery();

  const [countryId, setCountryId] = React.useState<string>(ALL_COUNTRIES_ID);
  const [selectedCs, setSelectedCs] = React.useState<CoordinateSystemItem | undefined>();
  const [selectedGeoid, setSelectedGeoid] = React.useState<CoordinateSystemItem | undefined>();

  const hasAppliedDefaultCountryRef = React.useRef(false);

  React.useEffect(() => {
    if (!isDefined(data) || hasAppliedDefaultCountryRef.current) {
      return;
    }
    hasAppliedDefaultCountryRef.current = true;

    if (i18n.language === 'sv') {
      const sweden = data.countries.find((c) => c.name === 'Sweden');
      if (sweden) {
        setCountryId(sweden.id);
      }
    }
  }, [data, i18n.language]);

  const countries = useMemo(
    () =>
      (data?.countries ?? []).map((c) =>
        c.id === ALL_COUNTRIES_ID
          ? { ...c, name: t('projectSettings.projection.allCountries', { ns: 'components' }) }
          : c,
      ),
    [data?.countries, t],
  );

  const filteredCs = useMemo(
    () => getFilteredItems(data?.coordinateSystems ?? [], selectedCs, countryId),
    [data?.coordinateSystems, selectedCs, countryId],
  );

  const filteredGeoids = useMemo(
    () => getFilteredItems(data?.geoids ?? [], selectedGeoid, countryId),
    [data?.geoids, selectedGeoid, countryId],
  );

  const handleCountryChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setCountryId(e.target.value);
  }, []);

  const handleCsSelected = useCallback(
    (suggestion: CoordinateSystemItem) => {
      setSelectedCs(suggestion);
      onCoordinateSystemChange?.(suggestion.id);
    },
    [onCoordinateSystemChange],
  );

  const handleCsCleared = useCallback(() => {
    setSelectedCs(undefined);
    onCoordinateSystemChange?.(null);
  }, [onCoordinateSystemChange]);

  const handleGeoidSelected = useCallback(
    (suggestion: CoordinateSystemItem) => {
      setSelectedGeoid(suggestion);
      onGeoidChange?.(suggestion.id);
    },
    [onGeoidChange],
  );

  const handleGeoidCleared = useCallback(() => {
    setSelectedGeoid(undefined);
    onGeoidChange?.(null);
  }, [onGeoidChange]);

  return (
    <OverlayLoader visible={isLoading || isDefined(errorList)}>
      <Stack spacing={1}>
        <LabelledContainer text={t('projectSettings.projection.country', { ns: 'components' })}>
          {(formElementId) => (
            <Select
              disabled={disabled}
              id={formElementId()}
              options={countries}
              value={countryId}
              onChange={handleCountryChange}
            />
          )}
        </LabelledContainer>

        <LabelledContainer text={t('projectSettings.projection.title', { ns: 'components' })}>
          {(formElementId) => (
            <DropdownSuggestion
              disabled={disabled}
              id={formElementId()}
              placeholder={t('notSelected', { ns: 'common' })}
              portalToBody={true}
              suggestions={filteredCs}
              value={selectedCs?.id}
              width="100%"
              onClear={handleCsCleared}
              onSuggestionSelected={handleCsSelected}
            />
          )}
        </LabelledContainer>

        <LabelledContainer text={t('projectSettings.projection.geoidModel', { ns: 'components' })}>
          {(formElementId) => (
            <>
              <DropdownSuggestion
                disabled={disabled}
                id={formElementId()}
                placeholder={t('notSelected', { ns: 'common' })}
                portalToBody={true}
                suggestions={filteredGeoids}
                value={selectedGeoid?.id}
                width="100%"
                onClear={handleGeoidCleared}
                onSuggestionSelected={handleGeoidSelected}
              />
              <GeoidAttribution geoid={selectedGeoid} />
            </>
          )}
        </LabelledContainer>

        {errorList}
      </Stack>
    </OverlayLoader>
  );
};
